// src/client/screens/borrower/CreditScoreScreen.tsx
// Shows the borrower's current credit score, tier, and score event history
// grouped into positive and negative factors.
import { CheckCircle2, XCircle } from 'lucide-react';
import { useCreditScore, useCreditScoreHistory } from '@client/hooks/useCreditScore';
import AppHeader from '@client/components/AppHeader';
import NavBar from '@client/components/NavBar';
import type { CreditTier } from '@client/lib/creditScore.types';
import type { CreditScoreEvent } from '@client/services/creditScoreService';

// ---------------------------------------------------------------------------
// Circular gauge SVG — full 360° track with a blue arc.
// ---------------------------------------------------------------------------

function ScoreCircle({ score }: { score: number }) {
  const R = 72;
  const STROKE = 10;
  const CIRC = 2 * Math.PI * R;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const dashArr = `${pct * CIRC} ${CIRC}`;
  const SIZE = (R + STROKE) * 2 + 4;

  return (
    <div style={{ position: 'relative', width: SIZE, height: SIZE, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
      <svg
        width={SIZE}
        height={SIZE}
        aria-label={`Credit score ${score} of 100`}
        role="img"
        style={{ position: 'absolute', top: 0, left: 0 }}
      >
        {/* Track */}
        <circle
          cx={SIZE / 2} cy={SIZE / 2} r={R}
          stroke="var(--c-border)" strokeWidth={STROKE} fill="none"
        />
        {/* Fill arc — starts at 12 o'clock */}
        <circle
          cx={SIZE / 2} cy={SIZE / 2} r={R}
          stroke="var(--c-primary-700)" strokeWidth={STROKE} fill="none"
          strokeDasharray={dashArr}
          strokeLinecap="round"
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
        />
      </svg>

      {/* Score text */}
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        <p style={{ margin: 0, fontSize: 36, fontWeight: 800, color: 'var(--c-text)', lineHeight: 1 }}>{score}</p>
        <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--c-text-muted)' }}>of 100</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tier badge
// ---------------------------------------------------------------------------

const TIER_LABEL: Record<CreditTier, string> = {
  new: 'New',
  building: 'Building',
  established: 'Established',
  strong: 'Strong',
  excellent: 'Excellent',
};

// ---------------------------------------------------------------------------
// Score event row
// ---------------------------------------------------------------------------

function EventRow({ event }: { event: CreditScoreEvent }) {
  const isPositive = event.delta >= 0;
  const sign = isPositive ? '+' : '';
  const deltaColor = isPositive ? 'var(--c-success)' : 'var(--c-danger)';
  const bgColor = isPositive ? 'var(--c-success-bg)' : 'var(--c-danger-bg)';
  const Icon = isPositive ? CheckCircle2 : XCircle;

  // Human-readable reason from the snake_case event key
  const reasonLabel = event.reason
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0' }}>
      <div style={{
        width: 36, height: 36,
        borderRadius: 10,
        background: bgColor,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon size={18} color={isPositive ? 'var(--c-success)' : 'var(--c-danger)'} strokeWidth={2} />
      </div>
      <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: 'var(--c-text)' }}>
        {reasonLabel}
      </span>
      <span style={{ fontSize: 14, fontWeight: 700, color: deltaColor, flexShrink: 0 }}>
        {sign}{event.delta}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export default function CreditScoreScreen() {
  const { data: score, isLoading: scoreLoading } = useCreditScore();
  const { data: history } = useCreditScoreHistory();

  const positiveEvents = (history ?? []).filter((e) => e.delta > 0);
  const negativeEvents = (history ?? []).filter((e) => e.delta < 0);

  return (
    <div className="app-shell app-shell--with-nav">
      <AppHeader />

      <main className="scroll-area" style={{ padding: '0 16px 24px' }}>
        <h1 style={{ margin: '16px 0 20px', fontSize: 26, fontWeight: 800, color: 'var(--c-text)' }}>
          Credit Score
        </h1>

        {/* Score gauge card */}
        <div style={{
          background: '#ffffff',
          borderRadius: 20,
          border: '1px solid var(--c-border)',
          padding: '28px 20px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          marginBottom: 16,
          boxShadow: '0 2px 12px rgba(15,26,44,0.05)',
        }}>
          {scoreLoading ? (
            <p style={{ color: 'var(--c-text-muted)', padding: '20px 0' }}>Loading…</p>
          ) : score ? (
            <>
              <ScoreCircle score={score.score} />

              {/* Tier badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 16, color: 'var(--c-text-dim)' }}>★</span>
                <span style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--c-text-sec)',
                  background: 'var(--c-surface-alt)',
                  borderRadius: 20,
                  padding: '4px 12px',
                }}>
                  {TIER_LABEL[score.tier]}
                </span>
              </div>
            </>
          ) : (
            <p style={{ color: 'var(--c-text-muted)', padding: '20px 0' }}>No score yet</p>
          )}
        </div>

        {/* Positive factors */}
        {positiveEvents.length > 0 && (
          <div style={{
            background: '#ffffff',
            borderRadius: 20,
            border: '1px solid var(--c-border)',
            padding: '16px 20px',
            marginBottom: 12,
            boxShadow: '0 2px 12px rgba(15,26,44,0.05)',
          }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: 'var(--c-success)' }}>
              Positive
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {positiveEvents.map((e) => (
                <div key={e.id} style={{ borderTop: '1px solid var(--c-border-lt)' }}>
                  <EventRow event={e} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Negative factors */}
        {negativeEvents.length > 0 && (
          <div style={{
            background: '#ffffff',
            borderRadius: 20,
            border: '1px solid var(--c-border)',
            padding: '16px 20px',
            marginBottom: 12,
            boxShadow: '0 2px 12px rgba(15,26,44,0.05)',
          }}>
            <h2 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: 'var(--c-danger)' }}>
              Negative
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {negativeEvents.map((e) => (
                <div key={e.id} style={{ borderTop: '1px solid var(--c-border-lt)' }}>
                  <EventRow event={e} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state for new users */}
        {!scoreLoading && score && positiveEvents.length === 0 && negativeEvents.length === 0 && (
          <div style={{
            background: '#ffffff',
            borderRadius: 16,
            border: '1px solid var(--c-border)',
            padding: '28px 20px',
            textAlign: 'center',
          }}>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--c-text-muted)' }}>
              No score events yet. Take your first microloan to start building your credit.
            </p>
          </div>
        )}
      </main>

      <NavBar />
    </div>
  );
}
